import { formatCurrency, formatPercent, formatRatio } from "~/lib/format";
import type { FinancialRatios, YearSummary } from "~/types/financials";
import { ChangeIndicator } from "./change-indicator";

interface RatioCardsProps {
	years: YearSummary[];
}

interface RatioGroup {
	label: string;
	ratios: {
		label: string;
		key: keyof FinancialRatios;
		format: (v: number | null) => string;
	}[];
}

const RATIO_GROUPS: RatioGroup[] = [
	{
		label: "Likvidita",
		ratios: [
			{ label: "Běžná likvidita", key: "currentRatio", format: formatRatio },
			{ label: "Pohotová likvidita", key: "quickRatio", format: formatRatio },
			{ label: "Cash flow likvidita", key: "cashFlowLiquidity", format: formatRatio },
		],
	},
	{
		label: "Ziskovost",
		ratios: [
			{ label: "ROE", key: "returnOnEquity", format: formatPercent },
			{ label: "ROA", key: "returnOnAssets", format: formatPercent },
			{ label: "Zisková marže", key: "profitMargin", format: formatPercent },
			{ label: "EBITDA marže", key: "ebitdaMargin", format: formatPercent },
			{ label: "Provozní marže", key: "ebitMargin", format: formatPercent },
			{ label: "Hrubá marže", key: "grossMargin", format: formatPercent },
			{ label: "ROCE", key: "roce", format: formatPercent },
		],
	},
	{
		label: "Zadluženost",
		ratios: [
			{ label: "Dluh / VK", key: "debtToEquity", format: formatRatio },
			{ label: "Míra zadluženosti", key: "debtRatio", format: formatPercent },
			{ label: "Míra samofinancování", key: "equityRatio", format: formatPercent },
			{ label: "Čistý pracovní kapitál", key: "netWorkingCapital", format: formatCurrency },
		],
	},
	{
		label: "Efektivita",
		ratios: [
			{ label: "Obrat aktiv", key: "assetTurnover", format: formatRatio },
			{ label: "Obrat stálých aktiv", key: "fixedAssetTurnover", format: formatRatio },
			{ label: "Obrat oběžných aktiv", key: "currentAssetTurnover", format: formatRatio },
			{ label: "Tržby / zaměstnance", key: "revenuePerEmployee", format: formatCurrency },
		],
	},
	{
		label: "Cash Flow",
		ratios: [
			{ label: "CF marže", key: "cashFlowMargin", format: formatPercent },
			{ label: "CF výnosnost aktiv", key: "cashFlowRoa", format: formatPercent },
			{ label: "Dluh / CF", key: "debtToCashFlow", format: formatRatio },
			{ label: "Samofinancování investic", key: "cashFlowSelfFinancing", format: formatRatio },
		],
	},
	{
		label: "Altman Z-Score",
		ratios: [
			{ label: "Z-Score", key: "altmanZScore", format: formatRatio },
			{ label: "X1 (prac. kapitál/aktiva)", key: "altmanX1", format: formatRatio },
			{ label: "X3 (EBIT/aktiva)", key: "altmanX3", format: formatRatio },
			{ label: "X4 (VK/dluhy)", key: "altmanX4", format: formatRatio },
		],
	},
];

function ratioChange(
	current: FinancialRatios,
	prior: FinancialRatios | null,
	key: keyof FinancialRatios,
): number | null {
	if (!prior) return null;
	const curr = current[key];
	const prev = prior[key];
	if (curr == null || prev == null || prev === 0) return null;
	return (curr - prev) / Math.abs(prev);
}

function AltmanBadge({ zScore }: { zScore: number | null }) {
	if (zScore == null) return null;

	let label: string;
	let className: string;
	if (zScore > 2.9) {
		label = "Bezpečná zóna";
		className = "bg-success/15 text-success";
	} else if (zScore >= 1.23) {
		label = "Šedá zóna";
		className = "bg-warning/15 text-warning";
	} else {
		label = "Tísňová zóna";
		className = "bg-error/15 text-error";
	}

	return (
		<span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>
			{label}
		</span>
	);
}

export function RatioCards({ years }: RatioCardsProps) {
	const latest = years[years.length - 1];
	const prior = years.length > 1 ? years[years.length - 2] : null;

	if (!latest) return null;

	return (
		<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
			{RATIO_GROUPS.map((group) => (
				<div key={group.label} className="card-surface rounded-xl p-4">
					<div className="flex items-center justify-between mb-3">
						<h4 className="text-sm font-semibold">{group.label}</h4>
						{group.label === "Altman Z-Score" && (
							<AltmanBadge zScore={latest.ratios.altmanZScore} />
						)}
					</div>
					<div className="flex flex-col gap-2">
						{group.ratios.map((ratio) => (
							<div key={ratio.key} className="flex items-center justify-between text-sm">
								<span className="text-muted-foreground">{ratio.label}</span>
								<div className="flex items-center gap-2">
									<span className="font-mono tabular-nums font-medium">
										{ratio.format(latest.ratios[ratio.key])}
									</span>
									<ChangeIndicator
										value={ratioChange(latest.ratios, prior?.ratios ?? null, ratio.key)}
									/>
								</div>
							</div>
						))}
					</div>
				</div>
			))}
		</div>
	);
}
